import { useCallback, useEffect, useRef, useState } from 'react';

import { isSolvedBoard, moveTile } from '@/lib/sliding-puzzle/board';
import type {
  GameBoard,
  GameScoreboard,
  GameStatusResponse,
  GameStatusValue,
  RoundScore,
} from '@/types/sliding-puzzle';
import { GameStatus } from '@/types/sliding-puzzle';

import { type BoardDifficulty } from '../engine/board-pool';
import { gameEngine } from '../engine/game-engine';

// ── Phase ────────────────────────────────────────────────────────────────────
export type GamePhase =
  | 'idle'
  | 'loading'
  | 'memorizing'
  | 'shuffle-animation'
  | 'playing'
  | 'board-clearing'
  | 'loading-next'
  | 'revealing'
  | 'calculating_result'
  | 'completed'
  | 'expired'
  | 'error';

// ── Board UI State ───────────────────────────────────────────────────────────
export interface BoardState {
  board: GameBoard;
  pieces: number[];
  totalRounds: number;
}

// ── Hook Return ──────────────────────────────────────────────────────────────
export interface UseGameSessionReturn {
  phase: GamePhase;
  boardState: BoardState | null;
  expiryAtMs: number | null;
  remainingMs: number;
  roundScores: RoundScore[];
  scoreboard: GameScoreboard | null;
  finalStatus: number | null;
  error: string | null;
  isOnline: boolean;
  isAllRoundsCleared: boolean;
  pendingCount: number;
  lives: number;
  flushAll: () => void;
  startGame: (options?: { resume?: boolean; enableTimer?: boolean; timeLimit?: number; difficulty?: BoardDifficulty }) => void;
  handleGameEnd: (status: GameStatusResponse) => void;
  handleTileClick: (slot: number) => void;
  onMemorizeComplete: () => void;
  onShuffleAnimationComplete: () => void;
  onRevealComplete: () => void;
  forfeit: () => void;
  autoSolve: () => void;
  skipLevel: () => void;
}

// ── URL params (kept for backward compatibility, but not used for API calls) ─
export interface GameSessionUrlParams {
  stageId: string;
  gameSessionId: string;
  key: string;
}

const TERMINAL_STATUSES: Set<GameStatusValue> = new Set([
  GameStatus.ENDED,
  GameStatus.EXPIRED,
  GameStatus.MANUALLY_ENDED,
]);

export function useGameSession(_urlParams: GameSessionUrlParams): UseGameSessionReturn {
  const [phase, setPhase] = useState<GamePhase>('idle');
  const [boardState, setBoardState] = useState<BoardState | null>(null);
  const [expiryAtMs, setExpiryAtMs] = useState<number | null>(null);
  const [remainingMs, setRemainingMs] = useState(0);
  const [roundScores, setRoundScores] = useState<RoundScore[]>([]);
  const [scoreboard, setScoreboard] = useState<GameScoreboard | null>(null);
  const [finalStatus, setFinalStatus] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isAllRoundsCleared, setIsAllRoundsCleared] = useState(false);
  const [lives, setLives] = useState(3);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const clearingRef = useRef(false);
  const finalizingRef = useRef(false);
  const revealCompletionRef = useRef<(() => void) | null>(null);

  // ── Expiry timer ──────────────────────────────────────────────────────────
  const startExpiryTimer = useCallback((expiryMs: number) => {
    if (timerRef.current) clearInterval(timerRef.current);

    const tick = () => {
      const left = Math.max(0, expiryMs - Date.now());
      setRemainingMs(left);
      if (left <= 0 && timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };

    tick();
    timerRef.current = setInterval(tick, 250);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Handle game end ───────────────────────────────────────────────────────
  const handleGameEnd = useCallback(
    (status: GameStatusResponse) => {
      stopTimer();
      setScoreboard(status.scoreboard ?? null);
      setFinalStatus(status.status);

      if (status.scoreboard?.rounds) {
        setRoundScores(status.scoreboard.rounds);
      }

      setPhase(
        status.status === GameStatus.ENDED || status.status === GameStatus.MANUALLY_ENDED
          ? 'completed'
          : 'expired',
      );
    },
    [stopTimer],
  );

  // ── Load a board ──────────────────────────────────────────────────────────
  const loadBoard = useCallback(
    (board: GameBoard, totalRounds: number) => {
      const pieces = [...board.pieces];
      setBoardState({ board, pieces, totalRounds });
      clearingRef.current = false;

      setPhase(board.displayTime > 0 ? 'memorizing' : 'shuffle-animation');
    },
    [],
  );

  // ── Finalize expired session ──────────────────────────────────────────────
  const finalizeExpiredSession = useCallback(() => {
    if (finalizingRef.current) return;
    finalizingRef.current = true;

    setPhase('calculating_result');
    stopTimer();

    gameEngine.endGame('expired');
    const status = gameEngine.getStatusResponse();
    handleGameEnd(status);
    finalizingRef.current = false;
  }, [handleGameEnd, stopTimer]);

  // ── Timer expiry effect ───────────────────────────────────────────────────
  useEffect(() => {
    if (remainingMs <= 0 && expiryAtMs && phase === 'playing') {
      stopTimer();
      finalizeExpiredSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remainingMs, expiryAtMs, phase]);

  // ── Start game ────────────────────────────────────────────────────────────
  const startGame = useCallback(
    (_options?: { resume?: boolean; enableTimer?: boolean; timeLimit?: number; difficulty?: BoardDifficulty }) => {
      setPhase('loading');
      setError(null);
      setRoundScores([]);
      setScoreboard(null);
      setFinalStatus(null);
      setIsAllRoundsCleared(false);

      try {
        gameEngine.reset();
        const params = new URLSearchParams(window.location.search);
        const boardCountParam = params.get('boardCount');
        const parsedBoardCount = boardCountParam ? parseInt(boardCountParam, 10) : undefined;
        const boardCount = typeof parsedBoardCount === 'number' && !Number.isNaN(parsedBoardCount) && parsedBoardCount > 0 ? parsedBoardCount : undefined;

        const session = gameEngine.startSession({
          enableTimer: _options?.enableTimer,
          timeLimit: _options?.timeLimit,
          difficulty: _options?.difficulty,
          boardCount,
        });

        if (TERMINAL_STATUSES.has(session.status)) {
          const status = gameEngine.getStatusResponse();
          handleGameEnd(status);
          return;
        }

        const state = gameEngine.getState();
        if (state && state.enableTimer) {
          const expMs = state.expiryAtMs;
          setExpiryAtMs(expMs);
          startExpiryTimer(expMs);
        } else {
          setExpiryAtMs(null);
        }

        setLives(gameEngine.getLives());

        loadBoard(session.board, session.totalRounds);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'Failed to start game';
        setError(msg);
        setPhase('error');
      }
    },
    [handleGameEnd, loadBoard, startExpiryTimer],
  );

  // ── Memorize complete ─────────────────────────────────────────────────────
  const onMemorizeComplete = useCallback(() => {
    setPhase('shuffle-animation');
  }, []);

  const onShuffleAnimationComplete = useCallback(() => {
    setPhase('playing');
  }, []);

  const onRevealComplete = useCallback(() => {
    const callback = revealCompletionRef.current;
    if (!callback) return;
    revealCompletionRef.current = null;
    callback();
  }, []);

  // ── Board cleared ─────────────────────────────────────────────────────────
  const onBoardCleared = useCallback(() => {
    if (clearingRef.current || finalizingRef.current || !boardState) return;
    clearingRef.current = true;

    setPhase('revealing');

    const currentRound = boardState.board.roundNumber;
    const totalRounds = boardState.totalRounds;
    const isLastBoard = currentRound >= totalRounds;

    if (isLastBoard) {
      setIsAllRoundsCleared(true);

      revealCompletionRef.current = () => {
        setPhase('calculating_result');
        const result = gameEngine.endBoard();
        setRoundScores((prev) => [
          ...prev,
          { roundNumber: currentRound, score: result.roundScore },
        ]);
        const status = gameEngine.getStatusResponse();
        handleGameEnd(status);
      };
    } else {
      revealCompletionRef.current = () => {
        setPhase('loading-next');
        const result = gameEngine.endBoard();
        setRoundScores((prev) => [
          ...prev,
          { roundNumber: currentRound, score: result.roundScore },
        ]);

        if (result.gameOver) {
          const status = gameEngine.getStatusResponse();
          handleGameEnd(status);
          return;
        }

        const next = gameEngine.getNextBoard();
        if (next) {
          loadBoard(next, totalRounds);
        }
      };
    }
  }, [boardState, handleGameEnd, loadBoard]);

  useEffect(() => {
    if (phase === 'playing' && boardState && isSolvedBoard(boardState.pieces)) {
      onBoardCleared();
    }
  }, [boardState, onBoardCleared, phase]);

  // ── Handle tile click ─────────────────────────────────────────────────────
  const handleTileClick = useCallback(
    (slot: number) => {
      if (phase !== 'playing' || !boardState) return;

      const { pieces } = boardState;
      const columns = boardState.board.gridSize.x;

      if (pieces[slot] === -1) return;

      const result = moveTile(pieces, slot, columns);
      if (!result.moved) return;

      const nextPieces = result.board;
      setBoardState((prev) => (prev ? { ...prev, pieces: nextPieces } : prev));

      // Update the engine's state
      gameEngine.updateCurrentPieces(nextPieces);
      gameEngine.recordMove();

      if (isSolvedBoard(nextPieces)) {
        onBoardCleared();
      }
    },
    [boardState, onBoardCleared, phase],
  );

  // ── Forfeit ───────────────────────────────────────────────────────────────
  const forfeit = useCallback(() => {
    stopTimer();
    gameEngine.endGame('forfeit');
    const status = gameEngine.getStatusResponse();
    handleGameEnd(status);
  }, [handleGameEnd, stopTimer]);

  // ── Auto solve (devtools) ─────────────────────────────────────────────────
  const autoSolve = useCallback(() => {
    if (phase !== 'playing' || !boardState) return;

    const n = boardState.pieces.length;
    const columns = boardState.board.gridSize.x;
    const pieces = Array.from({ length: n }, (_, i) => (i === n - 1 ? -1 : i));

    // To leave exactly 3 moves left, we perform 3 valid "backwards" swaps.
    if (n >= columns * 2 && columns >= 2) {
      const i1 = n - 1;
      const i2 = n - 2;
      const i3 = i2 - columns;
      const i4 = i3 + 1;

      [pieces[i1], pieces[i2]] = [pieces[i2], pieces[i1]];
      [pieces[i2], pieces[i3]] = [pieces[i3], pieces[i2]];
      [pieces[i3], pieces[i4]] = [pieces[i4], pieces[i3]];
    } else if (n >= 2) {
      [pieces[n - 1], pieces[n - 2]] = [pieces[n - 2], pieces[n - 1]];
    }

    setBoardState((prev) => (prev ? { ...prev, pieces } : prev));
    gameEngine.updateCurrentPieces(pieces);
  }, [boardState, phase]);

  // ── Skip Level ────────────────────────────────────────────────────────────
  const skipLevel = useCallback(() => {
    if (clearingRef.current || finalizingRef.current || !boardState || !gameEngine.canSkip()) return;
    clearingRef.current = true;
    setPhase('loading-next');

    const currentRound = boardState.board.roundNumber;
    const totalRounds = boardState.totalRounds;

    const result = gameEngine.endBoard(true);
    setRoundScores((prev) => [
      ...prev,
      { roundNumber: currentRound, score: result.roundScore },
    ]);

    if (result.gameOver) {
      setPhase('calculating_result');
      const status = gameEngine.getStatusResponse();
      handleGameEnd(status);
      return;
    }

    const next = gameEngine.getNextBoard();
    if (next) {
      loadBoard(next, totalRounds);
    }
    setLives(gameEngine.getLives());
    clearingRef.current = false;
  }, [boardState, handleGameEnd, loadBoard]);

  // Flush all and return to idle state
  const flushAll = useCallback(() => {
    setPhase('idle');
    setBoardState(null);
    setScoreboard(null);
    setFinalStatus(null);
    setRoundScores([]);
    setError(null);
    setLives(3);
  }, []);

  return {
    phase,
    boardState,
    expiryAtMs,
    remainingMs,
    roundScores,
    scoreboard,
    finalStatus,
    error,
    isOnline: true, // Always "online" in offline mode
    isAllRoundsCleared,
    pendingCount: 0,
    lives,
    flushAll,
    startGame,
    handleGameEnd,
    handleTileClick,
    onMemorizeComplete,
    onShuffleAnimationComplete,
    onRevealComplete,
    forfeit,
    autoSolve,
    skipLevel,
  };
}
